import React, { useRef, useEffect, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import TextUnit from "./TextUnit";
import Molecule from "./Molecule";

type BBox = [number, number, number, number];

type ObjectData = {
  cls: "text_unit" | "molecule" | "table";
  bbox: BBox;
  metadata?: {
    text?: string;
    smiles?: string;
  };
};

type ImageDisplayProps = {
  data: { objects: ObjectData[] } | null;
  image: HTMLImageElement | null;
};

type DisplayObject = {
  cls: "text_unit" | "molecule" | "table";
  x: number;
  y: number;
  width: number;
  height: number;
  transcription?: string | undefined;
};

const ImageDisplay: React.FC<ImageDisplayProps> = ({ data, image }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [imageProps, setImageProps] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [objects, setObjects] = useState<DisplayObject[]>([]);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setStageSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    if (
      !image ||
      !image.naturalWidth ||
      !image.naturalHeight ||
      stageSize.width === 0 ||
      stageSize.height === 0
    ) {
      setImageProps({ x: 0, y: 0, width: 0, height: 0 });
      return;
    }

    const scale = Math.min(
      stageSize.width / image.naturalWidth,
      stageSize.height / image.naturalHeight
    );
    const newWidth = image.naturalWidth * scale;
    const newHeight = image.naturalHeight * scale;
    const newX = (stageSize.width - newWidth) / 2;
    const newY = (stageSize.height - newHeight) / 2;

    setImageProps({ x: newX, y: newY, width: newWidth, height: newHeight });
  }, [image, stageSize]);

  useEffect(() => {
    if (data?.objects && imageProps.width > 0 && imageProps.height > 0) {
      setObjects(
        data.objects.map((object) => {
          const [x1, y1, x2, y2] = object.bbox;
          const transcription =
            object.metadata?.text || object.metadata?.smiles || undefined;

          return {
            cls: object.cls,
            x: x1 * imageProps.width + imageProps.x,
            y: y1 * imageProps.height + imageProps.y,
            width: (x2 - x1) * imageProps.width,
            height: (y2 - y1) * imageProps.height,
            transcription,
          };
        })
      );
    } else {
      setObjects([]);
    }
  }, [data, imageProps]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full flex items-center justify-center overflow-hidden"
    >
      <TransformWrapper>
        <TransformComponent>
          <div
            className="relative"
            style={{ width: stageSize.width, height: stageSize.height }}
          >
            {image && (
              <img
                src={image.src}
                alt="Document"
                className="absolute select-none pointer-events-none border-spacing-2 border-2 border-black"
                style={{
                  left: imageProps.x,
                  top: imageProps.y,
                  width: imageProps.width,
                  height: imageProps.height,
                }}
              />
            )}
            {objects.map((object, idx) => {
              if (object.cls === "text_unit" && object.transcription) {
                return (
                  <TextUnit
                    key={idx}
                    object={{
                      x: object.x,
                      y: object.y,
                      width: object.width,
                      height: object.height,
                      transcription: object.transcription,
                    }}
                    id={idx}
                  />
                );
              }
              if (object.cls === "molecule" && object.transcription) {
                return (
                  <Molecule
                    key={idx}
                    object={{
                      x: object.x,
                      y: object.y,
                      width: object.width,
                      height: object.height,
                      transcription: object.transcription,
                    }}
                  />
                );
              }
              if (object.cls === "table") {
                return (
                  <Molecule
                    key={idx}
                    object={{
                      x: object.x,
                      y: object.y,
                      width: object.width,
                      height: object.height,
                      transcription: "table",
                    }}
                  />
                );
              }
              return null;
            })}
          </div>
        </TransformComponent>
      </TransformWrapper>
    </div>
  );
};

export default ImageDisplay;
